import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';

import {SelectedTreeItem} from '../../../+reports/reports.interfaces';
import {TreeNode} from '../../../shared/ui';
import {TreeItem} from '../../../shared/ui/tree-multi-select/treeview.component';
import {FieldReviewRow, IMAGE_FIELD_PREFIX} from '../../template-management.interfaces';
import {TemplateManagementService} from '../../template-management.service';

@Component({
  selector: 'gp-field-review-list',
  templateUrl: './field-review-list.component.html',
  styleUrls: ['./field-review-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldReviewListComponent {

  @Input()
  public rows: FieldReviewRow[] = [];

  @Input()
  public fieldTree: Array<TreeNode<TreeItem>> = [];

  @Output()
  public rowsChange = new EventEmitter<FieldReviewRow[]>();

  @Output()
  public locate = new EventEmitter<string>();

  public pickingToken: string = null;

  constructor(private templateManagementService: TemplateManagementService) {
  }

  public toggleSelected(row: FieldReviewRow): void {
    this.updateRow(row.token, {selected: !row.selected});
  }

  public togglePicker(row: FieldReviewRow): void {
    this.pickingToken = this.pickingToken === row.token ? null : row.token;
  }

  public onFieldPicked(row: FieldReviewRow, item: SelectedTreeItem): void {
    if (!item?.id) {
      return;
    }
    const node = this.templateManagementService.getSelectedFieldData(this.fieldTree, item);
    if (!node) {
      return;
    }
    this.pickingToken = null;
    const fieldName = node.data?.data?.types?.[0] === 'FILE' ? IMAGE_FIELD_PREFIX + node.value : node.value;
    if (row.fields.some((field) => field.fieldName === fieldName)) {
      return;
    }
    this.updateRow(row.token, {
      fields: [...row.fields, {
        fieldName,
        displayPath: this.templateManagementService.getPathFromReportingFields(this.fieldTree, node)
      }],
      selected: true,
      overridden: true
    });
  }

  public removeField(row: FieldReviewRow, fieldName: string): void {
    const fields = row.fields.filter((field) => field.fieldName !== fieldName);
    this.updateRow(row.token, {fields, selected: row.selected && fields.length > 0, overridden: true});
  }

  public badgeKey(row: FieldReviewRow): string {
    if (row.overridden) {
      return 'TEMPLATE.ai_review_changed';
    }
    return row.match ? `TEMPLATE.ai_review_match_${row.match.toLowerCase()}` : null;
  }

  /** Drives both the badge and the row's side stripe, so the two always agree. */
  public statusClass(row: FieldReviewRow): string {
    return row.overridden ? 'changed' : (row.match ?? 'NONE').toLowerCase();
  }

  public reasonKey(row: FieldReviewRow): string {
    if (!row.match) {
      return 'TEMPLATE.ai_review_not_matched';
    }
    return row.reason === 'AMBIGUOUS' ? 'TEMPLATE.ai_review_reason_ambiguous' : 'TEMPLATE.ai_review_reason_no_match';
  }

  public trackByToken(_index: number, row: FieldReviewRow): string {
    return row.token;
  }

  private updateRow(token: string, changes: Partial<FieldReviewRow>): void {
    this.rowsChange.emit(this.rows.map((row) => row.token === token ? {...row, ...changes} : row));
  }
}
